import { z } from "zod";
import { Id, NonEmpty } from "../../base";
import { ChapterRecordSchema, ChapterCreateSchema, ChapterPatchSchema, } from "../campaign/chapter";
import { TimestampSchema } from "../../epoch";
import { BadgeTierSchema } from "../../user/badge";
import { IdentityLevelSchema } from "../../user/identity";
import { DESCRIPTION_LIMIT, campaignStatusValues, blockchainNetworksValues, tokenKeyValues, } from "../../constant_values";
//#region Schema
//#region REWARD CONFIG
const RewardConfigServerOwnedSchema = z.strictObject({
    safeAddress: z.string().optional(),
    nftContractAddress: z.string().optional(),
    nftContractDeployedAt: TimestampSchema.optional(),
    nftDeploymentTxHash: z.string().optional(),
});
const TokenRewardConfigUserOwnedSchema = z.strictObject({
    rewardMethod: z.literal("leaderboard-raffle-token").optional(),
    distributionMethod: z.literal("leaderboard-raffle").default("leaderboard-raffle"),
    rewardType: z.literal("token").default("token"),
    blockchainNetwork: z.enum(blockchainNetworksValues),
    tokenType: z.enum(tokenKeyValues),
    totalTokenAmount: NonEmpty,
    numberOfGuaranteedWinners: NonEmpty,
});
const NftRewardConfigUserOwnedSchema = z.strictObject({
    rewardMethod: z.literal("completion-nft"),
    distributionMethod: z.literal("completion").default("completion"),
    rewardType: z.literal("nft").default("nft"),
    blockchainNetwork: z.enum(blockchainNetworksValues),
    tokenType: z.undefined().optional(),
    totalTokenAmount: z.undefined().optional(),
    numberOfGuaranteedWinners: z.undefined().optional(),
    nftName: NonEmpty.max(80),
    nftSymbol: NonEmpty.max(16),
    nftDescription: NonEmpty.max(500),
    nftImageUrl: NonEmpty,
    nftBaseUri: NonEmpty.optional(),
});
export const TokenRewardConfigCreateSchema = TokenRewardConfigUserOwnedSchema;
export const NftRewardConfigCreateSchema = NftRewardConfigUserOwnedSchema;
export const RewardConfigCreateSchema = z.union([
    TokenRewardConfigCreateSchema,
    NftRewardConfigCreateSchema,
]);
export const RewardConfigPatchSchema = z.union([
    TokenRewardConfigUserOwnedSchema.partial(),
    NftRewardConfigUserOwnedSchema.partial(),
]);
export const TokenRewardConfigRecordSchema = TokenRewardConfigUserOwnedSchema.extend(RewardConfigServerOwnedSchema.shape);
export const NftRewardConfigRecordSchema = NftRewardConfigUserOwnedSchema.extend(RewardConfigServerOwnedSchema.shape);
export const RewardConfigRecordSchema = z.union([
    TokenRewardConfigRecordSchema,
    NftRewardConfigRecordSchema,
]);
//#endregion REWARD CONFIG
//#region METADATA
const CampaignMetadataBaseSchema = z.strictObject({
    userRestriction: BadgeTierSchema.default("Normie"),
    minimumIdentityLevel: IdentityLevelSchema.default(1),
    rewardConfig: RewardConfigRecordSchema,
});
const CampaignMetadataPatchSchema = z
    .strictObject({
    userRestriction: BadgeTierSchema.optional(),
    minimumIdentityLevel: IdentityLevelSchema.optional(),
    rewardConfig: RewardConfigPatchSchema.optional(),
    chapters: z.array(ChapterPatchSchema).optional(),
    deleteChapterIds: z.array(Id).optional(),
    deleteQuestIds: z
        .array(z.strictObject({
        chapterId: Id,
        questId: Id,
    }))
        .optional(),
})
    .partial();
const CampaignMetadataCreateSchema = CampaignMetadataBaseSchema.extend({
    chapters: z
        .array(ChapterCreateSchema)
        .min(1, "At least 1 Chapter is required."),
});
const CampaignMetadataRecordSchema = CampaignMetadataBaseSchema.extend({
    chapters: z.array(ChapterRecordSchema),
});
//#endregion METADATA
export const CampaignStatusSchema = z.enum(campaignStatusValues);
//#region CAMPAIGN
const CampaignUserOwnedSchema = z.strictObject({
    communityId: Id,
    name: NonEmpty,
    description: NonEmpty.max(DESCRIPTION_LIMIT),
    startDate: TimestampSchema,
    endDate: TimestampSchema,
});
// CREATE
export const CampaignCreateSchema = CampaignUserOwnedSchema.extend({
    metadata: CampaignMetadataCreateSchema,
});
// UPDATE
export const CampaignPatchSchema = CampaignUserOwnedSchema.partial()
    .extend({
    id: Id,
    metadata: CampaignMetadataPatchSchema.optional(),
})
    .omit({
    communityId: true,
});
const CampaignServerOwnedSchema = z.strictObject({
    ownerId: Id,
    status: CampaignStatusSchema.default("draft"),
    statusUpdatedAt: TimestampSchema.optional(),
    publishedAt: TimestampSchema.optional(),
    endedAt: TimestampSchema.optional(),
    totalParticipants: z.number().optional().default(0),
    updatedAt: TimestampSchema.optional(),
    createdAt: TimestampSchema,
});
export const CampaignRecordSchema = CampaignUserOwnedSchema.extend({
    id: Id,
    metadata: CampaignMetadataRecordSchema,
}).extend(CampaignServerOwnedSchema.shape);
// VIEW
export const CampaignRecordPublicSchema = CampaignRecordSchema.omit({
    ownerId: true,
});
// FILTER VIEW
export const ListCampaignsSchema = z.strictObject({
    communityId: Id,
    limit: z
        .string()
        .optional()
        .transform((v) => (v ? Number(v) : undefined))
        .refine((v) => v === undefined || (!Number.isNaN(v) && v > 0), {
        message: "limit must be a positive number",
    }),
    cursor: z.string().optional(),
});
export const GetCampaignParamsSchema = z.strictObject({
    campaignId: Id,
});
//#endregion Types
